// MobileHome — the phone's front door: the flat, space-filterable list of
// every conversation, with a long-press action sheet per room.

import { CountBadge } from "~/components/mobile/CountBadge";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { BellOff, ChevronDown, Pencil, Search } from "lucide-react";

import { MobileActionSheet, type MobileSheetAction } from "~/components/mobile/MobileActionSheet";
import { NewConversationSheet } from "~/components/mobile/NewConversationSheet";
import { OrgMark, OrgMenu } from "~/components/mobile/OrgMenu";
import { MobileInvitations } from "~/components/mobile/MobileInvitations";
import { useMobileStatus } from "~/components/mobile/status";
import { mobileStrings } from "~/components/mobile/strings";
import { useMatrix } from "~/context/MatrixContext";
import { getInitials } from "~/lib/initials";
import { useToast } from "~/context/ToastContext";
import { useMobileHomeRooms } from "~/hooks/useMobileHomeRooms";
import { useSidebarLayout } from "~/hooks/useSidebarLayout";
import {
  foldDirectDuplicates,
  isCoreSection,
  isRoomHidden,
  type CoreSectionId,
} from "~/lib/sidebar-layout";
import {
  archiveRoom,
  sendReadReceipt,
  setRoomNotificationLevel,
  type RoomSummary,
} from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_TOLERANCE = 10;
/** Remembers the chosen org across launches. */
const ACTIVE_ORG_STORAGE_KEY = "chat.mobile.activeOrgSpaceId";

function readStoredOrg(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeOrg(spaceId: string | null): void {
  try {
    if (spaceId === null) window.localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    else window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, spaceId);
  } catch {
    /* storage unavailable: the choice lasts for this session only */
  }
}

/** Per-device collapse state; the section order and hidden rooms sync. */
const COLLAPSED_STORAGE_KEY = "chat.mobile.collapsedSections";
type HomeSectionId = CoreSectionId | "hidden" | "hidden-open";

interface HomeSection {
  id: HomeSectionId;
  /** The synced section this header reorders; `null` for Hidden. */
  arrangeable: CoreSectionId | null;
  title: string;
  items: RoomSummary[];
  collapsed: boolean;
}

function readCollapsedSections(): Set<HomeSectionId> {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(COLLAPSED_STORAGE_KEY) ?? "[]");
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter(
        (id): id is HomeSectionId => id === "channels" || id === "dms" || id === "hidden-open",
      ),
    );
  } catch {
    return new Set();
  }
}

function storeCollapsedSections(ids: Set<HomeSectionId>): void {
  try {
    window.localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    /* storage unavailable: collapse lasts for this session only */
  }
}

export interface MobileHomeProps {
  onOpenRoom: (roomId: string) => void;
  /** Opens global search. Stubbed by the shell until the search ticket. */
  onOpenSearch?: () => void;
  /** The room open beside the list (tablet split); its row shows selected. */
  activeRoomId?: string | null;
}

export function MobileHome({ onOpenRoom, onOpenSearch, activeRoomId = null }: MobileHomeProps) {
  const { state, client } = useMatrix();
  const [activeSpaceId, setActiveSpaceId] = useState<string | null>(readStoredOrg);
  const {
    spaces,
    rooms,
    mutedRoomIds,
    disambiguationByRoomId,
    unreadBySpace,
    dmStatus,
    retryDmRoster,
  } = useMobileHomeRooms(activeSpaceId);
  const [sheetRoom, setSheetRoom] = useState<RoomSummary | null>(null);
  const [composing, setComposing] = useState(false);
  const [choosingOrg, setChoosingOrg] = useState(false);
  const selectOrg = useCallback((spaceId: string | null) => {
    setActiveSpaceId(spaceId);
    storeOrg(spaceId);
  }, []);

  // A filter chip can vanish (the user left the space) while it is
  // selected. Fall back to "All" rather than render an empty list with
  // a highlighted chip the user can no longer see.
  const effectiveSpaceId = useMemo(
    () => (activeSpaceId && spaces.some((s) => s.id === activeSpaceId) ? activeSpaceId : null),
    [activeSpaceId, spaces],
  );

  const showError = state === "error";
  const { layout, moveSection: moveLayoutSection, setHidden } = useSidebarLayout();
  const coreOrder = useMemo(() => layout.order.filter(isCoreSection), [layout.order]);
  const [collapsed, setCollapsed] = useState<Set<HomeSectionId>>(readCollapsedSections);
  const [arrangingSection, setArrangingSection] = useState<CoreSectionId | null>(null);
  const toggleSection = useCallback((id: HomeSectionId) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      storeCollapsedSections(next);
      return next;
    });
  }, []);

  const sections = useMemo((): HomeSection[] => {
    const listed = foldDirectDuplicates(rooms);
    const shown = listed.filter((room) => !isRoomHidden(layout, room));
    const hidden = listed.filter((room) => isRoomHidden(layout, room));
    const byId: Record<CoreSectionId, RoomSummary[]> = {
      dms: shown.filter((room) => room.isDirect),
      channels: shown.filter((room) => !room.isDirect),
    };
    const titles: Record<CoreSectionId, string> = {
      dms: mobileStrings.home.sectionDirect,
      channels: mobileStrings.home.sectionChannels,
    };
    return [
      ...coreOrder.map(
        (id): HomeSection => ({
          id,
          arrangeable: id,
          title: titles[id],
          items: byId[id],
          collapsed: collapsed.has(id),
        }),
      ),
      {
        id: "hidden",
        arrangeable: null,
        title: mobileStrings.home.sectionHidden(hidden.length),
        items: hidden,
        collapsed: !collapsed.has("hidden-open"),
      },
    ];
  }, [rooms, layout, coreOrder, collapsed]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <HomeHeader
        orgMenu={
          choosingOrg ? (
            <OrgMenu
              spaces={spaces}
              activeSpaceId={effectiveSpaceId}
              unreadBySpace={unreadBySpace}
              onSelect={selectOrg}
              onClose={() => setChoosingOrg(false)}
            />
          ) : null
        }
        orgMenuOpen={choosingOrg}
        orgName={
          effectiveSpaceId === null
            ? null
            : (spaces.find((space) => space.id === effectiveSpaceId)?.name ?? null)
        }
        canSwitchOrg={spaces.length > 0}
        onSwitchOrg={() => setChoosingOrg(true)}
        onOpenSearch={onOpenSearch}
        onStartConversation={() => setComposing(true)}
      />

      {!showError && <MobileInvitations client={client} />}
      {showError ? (
        <HomeError />
      ) : rooms.length === 0 ? (
        dmStatus === "all" ? (
          <HomeEmpty />
        ) : (
          <HomeOrgStatus status={dmStatus} onRetry={retryDmRoster} />
        )
      ) : (
        <div
          data-conversation-list
          aria-label={mobileStrings.home.roomsLabel}
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-2"
        >
          {/* Proximity does the grouping: rows sit 44px apart, groups half
              as much again, so a section reads as one thing without a line. */}
          {sections.map((section) =>
            section.id === "dms" && section.items.length === 0 && dmStatus !== "all" ? (
              <section key={section.id} aria-label={section.title}>
                <SectionHeader
                  title={section.title}
                  collapsed={section.collapsed}
                  onToggle={() => toggleSection("dms")}
                  onArrange={() => setArrangingSection("dms")}
                />
                {!section.collapsed && <HomeOrgStatus status={dmStatus} onRetry={retryDmRoster} />}
              </section>
            ) : section.items.length === 0 ? null : (
              <section key={section.id} aria-label={section.title}>
                <SectionHeader
                  title={section.title}
                  collapsed={section.collapsed}
                  onToggle={() =>
                    toggleSection(section.id === "hidden" ? "hidden-open" : section.id)
                  }
                  onArrange={
                    section.arrangeable ? () => setArrangingSection(section.arrangeable) : undefined
                  }
                />
                {!section.collapsed && (
                  <ul
                    {...(section.id === "dms" ? { "data-cy": "org-dm-list" } : {})}
                    className={section.id === "hidden" ? "opacity-60" : undefined}
                  >
                    {section.items.map((room) => (
                      <li key={room.id}>
                        <RoomRow
                          room={room}
                          selected={room.id === activeRoomId}
                          orgSuffix={disambiguationByRoomId.get(room.id) ?? null}
                          muted={mutedRoomIds.has(room.id)}
                          onOpen={() => onOpenRoom(room.id)}
                          onActions={() => setSheetRoom(room)}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ),
          )}
        </div>
      )}
      {arrangingSection && (
        <MobileActionSheet
          label={mobileStrings.home.arrangeLabel(
            arrangingSection === "dms"
              ? mobileStrings.home.sectionDirect
              : mobileStrings.home.sectionChannels,
          )}
          actions={[
            {
              key: "up",
              label: mobileStrings.home.moveUp,
              disabled: coreOrder.indexOf(arrangingSection) === 0,
              onSelect: () => moveLayoutSection(arrangingSection, -1),
            },
            {
              key: "down",
              label: mobileStrings.home.moveDown,
              disabled: coreOrder.indexOf(arrangingSection) === coreOrder.length - 1,
              onSelect: () => moveLayoutSection(arrangingSection, 1),
            },
          ]}
          onClose={() => setArrangingSection(null)}
        />
      )}
      {sheetRoom && (
        <RoomActionSheet
          room={sheetRoom}
          hidden={layout.hidden.includes(sheetRoom.id)}
          onSetHidden={(hidden) => setHidden(sheetRoom.id, hidden)}
          muted={mutedRoomIds.has(sheetRoom.id)}
          onClose={() => setSheetRoom(null)}
        />
      )}
      {composing && (
        <NewConversationSheet onOpenRoom={onOpenRoom} onClose={() => setComposing(false)} />
      )}
    </div>
  );
}

function HomeHeader({
  orgMenu,
  orgMenuOpen,
  orgName,
  canSwitchOrg,
  onSwitchOrg,
  onOpenSearch,
  onStartConversation,
}: {
  /** The open org menu, rendered anchored under the title. */
  orgMenu: ReactNode;
  orgMenuOpen: boolean;
  /** The active org, or `null` for every org. */
  orgName: string | null;
  canSwitchOrg: boolean;
  onSwitchOrg: () => void;
  onOpenSearch?: () => void;
  onStartConversation?: () => void;
}) {
  const title = orgName ?? mobileStrings.home.allOrgs;
  // The connection speaks inside the header row; the list never moves.
  const status = useMobileStatus();
  return (
    <div className="relative flex h-14 shrink-0 items-center gap-1 pl-4 pr-1">
      {canSwitchOrg ? (
        <button
          type="button"
          onClick={onSwitchOrg}
          aria-haspopup="dialog"
          aria-expanded={orgMenuOpen}
          aria-label={mobileStrings.home.switchOrg(title)}
          className="-ml-1 flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-md pl-1 text-left outline-none focus-visible:shadow-neon-cyan"
        >
          <OrgMark name={orgName} size={28} />
          <span className="truncate text-lg font-light text-foreground/90">{title}</span>
          <ChevronDown
            aria-hidden
            size={16}
            strokeWidth={ICON_STROKE}
            className={`shrink-0 text-muted-foreground transition-transform ${
              orgMenuOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      ) : (
        <span className="flex-1 truncate text-lg font-light text-foreground/90">
          {mobileStrings.home.wordmark}
        </span>
      )}
      {status && (
        <span aria-hidden className="shrink-0 pl-2 text-xs font-light text-muted-foreground">
          {status}
        </span>
      )}
      <button
        type="button"
        onClick={onOpenSearch}
        aria-label={mobileStrings.home.search}
        className="flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none transition-opacity hover:text-foreground/90 focus-visible:shadow-neon-cyan"
      >
        <Search size={20} strokeWidth={ICON_STROKE} />
      </button>
      <button
        type="button"
        onClick={onStartConversation}
        aria-label={mobileStrings.home.compose}
        className="flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none transition-opacity hover:text-foreground/90 focus-visible:shadow-neon-cyan"
      >
        <Pencil size={20} strokeWidth={ICON_STROKE} />
      </button>
      {orgMenu}
    </div>
  );
}

function SectionHeader({
  title,
  collapsed,
  onToggle,
  onArrange,
}: {
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  /** Opens Move up / Move down; long-press or secondary click. */
  onArrange?: () => void;
}) {
  const { handlers: longPress, consumeClick } = useLongPress(onArrange ?? (() => {}));
  return (
    <h2>
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={() => {
          if (consumeClick()) return;
          onToggle();
        }}
        onContextMenu={(event) => {
          if (!onArrange) return;
          event.preventDefault();
          onArrange();
        }}
        {...(onArrange ? longPress : {})}
        className="flex h-11 w-full items-end gap-1.5 px-4 pb-2 text-left text-[10px] font-normal uppercase tracking-widest text-muted-quiet outline-none focus-visible:shadow-neon-cyan"
      >
        {title}
        <ChevronDown
          aria-hidden
          size={10}
          strokeWidth={ICON_STROKE}
          className={`mb-px shrink-0 transition-transform ${collapsed ? "-rotate-90" : ""}`}
        />
      </button>
    </h2>
  );
}

function RoomRow({
  room,
  selected,
  orgSuffix,
  muted,
  onOpen,
  onActions,
}: {
  room: RoomSummary;
  selected: boolean;
  /** Org name shown after the room name when another row shares the name. */
  orgSuffix: string | null;
  muted: boolean;
  onOpen: () => void;
  onActions: () => void;
}) {
  const isUnread = room.unreadTotal > 0;
  const isMention = room.unreadHighlight > 0;
  const { handlers: longPress, consumeClick } = useLongPress(onActions);

  const label = useMemo(() => {
    const prefix = room.isDirect ? room.name : `#${room.name}`;
    const parts: string[] = [orgSuffix ? `${prefix}, ${orgSuffix}` : prefix];
    if (isMention) {
      parts.push(
        room.unreadHighlight === 1
          ? mobileStrings.home.mentionsOne
          : mobileStrings.home.mentionsOther(room.unreadHighlight),
      );
    } else if (room.isDirect && isUnread) {
      parts.push(
        room.unreadTotal === 1
          ? mobileStrings.home.unreadOne
          : mobileStrings.home.unreadOther(room.unreadTotal),
      );
    }
    if (muted) parts.push(mobileStrings.sheet.mute);
    return parts.join(", ");
  }, [
    room.name,
    room.isDirect,
    room.unreadTotal,
    room.unreadHighlight,
    orgSuffix,
    isUnread,
    isMention,
    muted,
  ]);

  return (
    <button
      type="button"
      aria-label={label}
      aria-current={selected ? "true" : undefined}
      onClick={() => {
        if (consumeClick()) return;
        onOpen();
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        onActions();
      }}
      {...longPress}
      className={`flex h-11 w-full items-center gap-3 px-4 text-left outline-none transition-colors focus-visible:shadow-neon-cyan ${
        selected ? "bg-foreground/[0.06]" : "hover:bg-foreground/[0.03] active:bg-foreground/[0.06]"
      }`}
    >
      <RoomAvatar room={room} />
      <span
        aria-hidden
        data-feedback-redact="true"
        className={`min-w-0 flex-1 truncate text-base ${
          isUnread ? "text-foreground/90" : "text-foreground/70"
        }`}
      >
        <span
          data-conversation-name={room.id}
          className="inline-block max-w-full truncate align-bottom"
        >
          {room.name}
        </span>
        {orgSuffix && <span className="font-light text-muted-foreground"> · {orgSuffix}</span>}
      </span>
      {muted && (
        <BellOff
          aria-hidden
          size={12}
          strokeWidth={ICON_STROKE}
          className="shrink-0 text-muted-quiet"
        />
      )}
      {(room.isDirect || isMention) && (
        <CountBadge
          count={isMention ? room.unreadHighlight : room.unreadTotal}
          tone={muted ? "quiet" : isMention ? "accent" : "ink"}
          className="min-w-5 leading-5"
        />
      )}
    </button>
  );
}

function RoomAvatar({ room }: { room: RoomSummary }) {
  if (room.isDirect) {
    return (
      <span
        aria-hidden
        className="flex size-6 shrink-0 items-center justify-center rounded-full bg-foreground/[0.1] text-[10px] font-medium text-foreground/80"
      >
        {getInitials(room.name) || "?"}
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="flex size-6 shrink-0 items-center justify-center text-base font-light text-muted-foreground"
    >
      #
    </span>
  );
}

function RoomActionSheet({
  room,
  hidden,
  onSetHidden,
  muted,
  onClose,
}: {
  room: RoomSummary;
  hidden: boolean;
  onSetHidden: (hidden: boolean) => void;
  muted: boolean;
  onClose: () => void;
}) {
  const { client, userId, unreadGroups } = useMatrix();
  const { push: pushToast } = useToast();

  // Read the global unread digest (not the space-scoped `useUnreads`):
  // Home lists rooms across every space, so a scoped lookup would leave
  // "Mark read" disabled on a room that still shows an unread count.
  const lastUnreadEventId = useMemo(() => {
    const group = unreadGroups.find((g) => g.roomId === room.id);
    const last = group?.events[group.events.length - 1];
    return last?.id ?? null;
  }, [unreadGroups, room.id]);

  const actions: MobileSheetAction[] = [
    {
      key: "mute",
      label: muted ? mobileStrings.sheet.unmute : mobileStrings.sheet.mute,
      onSelect: () => {
        if (!client) return;
        void setRoomNotificationLevel(client, room.id, muted ? "mentions" : "mute").catch((err) => {
          console.error("setRoomNotificationLevel failed", err);
        });
      },
    },
    {
      key: "mark-read",
      label: mobileStrings.sheet.markRead,
      disabled: lastUnreadEventId === null,
      onSelect: () => {
        if (!client || lastUnreadEventId === null) return;
        void sendReadReceipt(client, room.id, lastUnreadEventId);
      },
    },
    {
      key: "hide",
      label: hidden ? mobileStrings.sheet.show : mobileStrings.sheet.hide,
      onSelect: () => onSetHidden(!hidden),
    },
    // Archiving closes the room for everyone, so only channels offer it.
    ...(room.isDirect
      ? []
      : [
          {
            key: "archive",
            label: mobileStrings.sheet.archive,
            destructive: true,
            onSelect: () => {
              if (!client || !userId) return;
              void archiveRoom(client, room.id, userId).catch((err) => {
                console.error("archiveRoom failed", err);
                pushToast({ message: mobileStrings.home.archiveError(room.name) });
              });
            },
          },
        ]),
  ];

  return (
    <MobileActionSheet
      label={mobileStrings.sheet.actionsLabel(room.isDirect ? room.name : `#${room.name}`)}
      actions={actions}
      onClose={onClose}
    />
  );
}

function HomeEmpty() {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center">
      <p className="text-sm font-light text-muted-foreground">{mobileStrings.home.empty}</p>
    </div>
  );
}

// Honest scoped-DM state for an org (pending / error / empty); rendered only
// when scoped, so "All" keeps every DM.
function HomeOrgStatus({
  status,
  onRetry,
}: {
  status: "pending" | "ready" | "empty" | "error";
  onRetry: () => void;
}) {
  const message =
    status === "pending"
      ? "Checking organisation members…"
      : status === "error"
        ? "Could not load organisation members."
        : "No conversations in this organisation.";
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 text-center">
      <p data-cy="org-dm-status" className="text-sm font-light text-muted-foreground">
        {message}
      </p>
      {status === "error" && (
        <button
          type="button"
          data-cy="org-dm-retry"
          onClick={onRetry}
          className="mt-3 rounded-md px-3 py-1.5 text-sm outline-none focus-visible:shadow-neon-cyan"
        >
          Retry
        </button>
      )}
    </div>
  );
}
function HomeError() {
  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="text-sm font-light text-muted-foreground">{mobileStrings.home.errorTitle}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-sm px-3 py-1.5 text-xs font-light text-muted-quiet outline-none transition-opacity hover:text-foreground/70 focus-visible:shadow-neon-cyan"
      >
        {mobileStrings.home.errorRetry}
      </button>
    </div>
  );
}

interface LongPressHandlers {
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: () => void;
  onPointerLeave: () => void;
}

/**
 * Fire `onLongPress` after a sustained press that doesn't drift past the
 * move tolerance. Touch-only affordance; the context-menu key gives
 * keyboard / AT users the same sheet, so this stays a pure enhancement.
 * `consumeClick` lets the row swallow the synthetic click a long press
 * leaves in its wake so the room doesn't also open underneath the sheet.
 */
function useLongPress(onLongPress: () => void): {
  handlers: LongPressHandlers;
  consumeClick: () => boolean;
} {
  const timerRef = useRef<number | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    originRef.current = null;
  }, []);

  // Clear a pending press timer if the row unmounts mid-press, so the
  // callback can't fire against an unmounted tree.
  useEffect(() => cancel, [cancel]);

  const consumeClick = useCallback(() => {
    if (!firedRef.current) return false;
    firedRef.current = false;
    return true;
  }, []);

  return {
    handlers: {
      onPointerDown: (event) => {
        firedRef.current = false;
        originRef.current = { x: event.clientX, y: event.clientY };
        timerRef.current = window.setTimeout(() => {
          timerRef.current = null;
          firedRef.current = true;
          onLongPress();
        }, LONG_PRESS_MS);
      },
      onPointerMove: (event) => {
        const origin = originRef.current;
        if (!origin) return;
        if (
          Math.abs(event.clientX - origin.x) > LONG_PRESS_MOVE_TOLERANCE ||
          Math.abs(event.clientY - origin.y) > LONG_PRESS_MOVE_TOLERANCE
        ) {
          cancel();
        }
      },
      onPointerUp: cancel,
      onPointerLeave: cancel,
    },
    consumeClick,
  };
}
